import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';

export type ReminderChannel = 'in_app' | 'email' | 'both';

export interface Reminder {
  id: string;
  tenant_id: string;
  user_id: string;
  title: string;
  notes: string | null;
  remind_at: string;
  timezone: string;
  entity_type: string | null;
  entity_id: string | null;
  channel: ReminderChannel;
  dispatched_at: string | null;
  rrule: string | null;
  next_remind_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateReminderInput {
  title: string;
  notes?: string | null;
  remind_at: string;
  timezone?: string;
  entity_type?: string | null;
  entity_id?: string | null;
  channel?: ReminderChannel;
  rrule?: string | null;
}

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(): SupabaseAuthLike {
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return {
      auth: {
        getSession: async () => ({ data: { session: null } }),
      },
    };
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const client = resolveClient();
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

async function apiDelete(path: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, { method: 'DELETE', headers });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
}

function useAsyncQuery<T>(
  fn: () => Promise<T>,
  initial: T,
  deps: readonly unknown[],
  enabled = true,
): { data: T; isLoading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(() => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    fn()
      .then((next) => {
        if (mountedRef.current) setData(next);
      })
      .catch((err) => {
        if (mountedRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error, refetch: run };
}

export function useReminders() {
  return useAsyncQuery<Reminder[]>(() => apiFetch<Reminder[]>('reminders'), [], []);
}

export function useCreateReminder() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (input: CreateReminderInput) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<Reminder>('reminders', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    } catch (err) {
      const next = err instanceof Error ? err : new Error(String(err));
      setError(next);
      throw next;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutate, isPending, error };
}

export function useUpdateReminder() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (input: { id: string } & Partial<CreateReminderInput>) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<Reminder>(`reminders/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
    } catch (err) {
      const next = err instanceof Error ? err : new Error(String(err));
      setError(next);
      throw next;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutate, isPending, error };
}

export function useDeleteReminder() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (id: string) => {
    setIsPending(true);
    setError(null);
    try {
      await apiDelete(`reminders/${id}`);
    } catch (err) {
      const next = err instanceof Error ? err : new Error(String(err));
      setError(next);
      throw next;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutate, isPending, error };
}
